// Typen der Kampfsimulation: Aufstellung rein, Ereignisprotokoll und Bilanz raus.
import type { EnemyBehavior, UnitStats } from '../content/index.ts';

/**
 * Was ein Freund aus dem Run in den Kampf mitbringt (R11, F7, I1): Werte nach Level, Items und Level-Knoten,
 * mitgenommene Verletzungen, veränderte Kniff-Zahlen und Sonderwirkungen. Die Run-Simulation rechnet das aus
 * den Daten aus (X3); der Kampf liest nur das Ergebnis.
 */
export type CombatProfile = {
  /** Q62: Die Größe kommt im Kampf immer von der Spezies; radius steht hier nur zur Anzeige */
  stats: Omit<UnitStats, 'radius'> & { radius?: number };
  /** S5 Waschbär: Heilung pro Angriff */
  attackHeal?: number;
  /** F35: Ein Ast kann den Kniff verwandeln */
  kniff?: string | null;
  /** F35 (Fabi): Verändert ein Ast, wie der Kniff wirkt, beschreibt er ihn neu (Text mit Platzhaltern) */
  kniffText?: string;
  /** Kniff- und Passiv-Zahlen dieses Freundes, Schlüssel „faehigkeit.wert“, z. B. „nussknacker.hits“ */
  params?: Record<string, number>;
  /** Sonderwirkungen aus Items und Knoten, z. B. revive (F22), selfDamage (I5), xpPerKniff (I6) */
  perks?: Record<string, number>;
  /** Fabi (Phase Kern): woher jede Sonderwirkung kommt, etwa lifesteal: ['item:holunderkette', 'node:...'] */
  perkFrom?: Record<string, string[]>;
  /** R11: Leben beim Betreten des Ufers */
  hp?: number;
  /** S5: Der Baum nimmt sein eigenes Leben mit in den nächsten Kampf */
  treeHp?: number;
  /** K46 Übungsufer: eine andere Passive als die der Spezies (Eisvogel mit Sturzflug) */
  passive?: string;
};

/** K2: freie Platzierung. Beim Eichhörnchen ist der Platz zugleich sein Baum (S5). */
export type FriendPlacement = {
  /** Kennung des Freundes im Run, kommt in der Bilanz zurück */
  id?: string;
  species: string;
  /** Standard: die erste Klasse der Spezies */
  friendClass?: string;
  /** Standard: der erste Kniff aus dem Pool der Klasse (F32, Q29) */
  kniff?: string;
  name?: string;
  profile?: CombatProfile;
  x: number;
  y: number;
  /** K27 (Fabi, Spieltest Phase 4): Marschrichtung, so weit läuft er zu Kampfbeginn; Länge je nach Tempo */
  march?: { x: number; y: number };
  /** F42 (Fabi, Phase Kern): kommt verwundet ans Ufer; fällt er, nimmt er Abschied */
  wounded?: boolean;
  /** S10 Leibwache (Fabi, Phase Kern): sein Schützling als Index in friends; ohne Angabe der Zerbrechlichste */
  ward?: number;
  /**
   * B25 (Fabi, 29.10.2026): Wer eine Schicht mit ihm im Gemeinschaftsraum war (Indizes in friends). Stehen sie im Kampf
   * nebeneinander (reach), macht jeder bonus mehr Schaden und steckt die Hälfte davon weniger ein
   */
  bond?: { with: number[]; bonus: number; reach: number };
};

/** E6, G4: ein befallenes Tier kämpft mit Kniff und Passive auf der Gegnerseite */
export type InfestedSpec = {
  species: string;
  friendClass: string;
  name: string;
  level: number;
  profile: CombatProfile;
};

/** K7: Gegner stehen vorher sichtbar fest, mit ihren Eigenschaften (K23). */
export type EnemyPlacement = {
  /** Sturmwesen aus enemies.json; leer bei einem Befallenen */
  enemy?: string;
  infested?: InfestedSpec;
  traits?: string[];
  /** R27: Gegner werden mit jedem Tag stärker, Faktor auf Leben und Schaden [ZAHL?] */
  statFactor?: number;
  /** G20 (Fabi): Jedes Sturmwesen ist ein bisschen anders, gewürfelt in einem Rahmen [ZAHL?] */
  hpRoll?: number;
  damageRoll?: number;
  /** I18: sichtbar getragenes Item, fällt beim Vertreiben, ist beim Entkommen weg; G21 (Runde 7): es stärkt seinen Träger */
  item?: string;
  /** I18 (Fabi, Runde 7): ganz selten ein zweites */
  items?: string[];
  x: number;
  y: number;
  /** K27: Manche Gegner laufen zu Kampfbeginn los, immer grob nach links */
  march?: { x: number; y: number };
  /** K46 Übungsufer: so schnell füllt sich sein Mut, Faktor [ZAHL?] */
  mutGain?: number;
  /** K46 Übungsufer: ein anderer Kniff als sonst (der Windschnitt springt immer wieder) */
  kniff?: string;
};

export type BattleSetup = {
  friends: FriendPlacement[];
  enemies: EnemyPlacement[];
  /** R25: Sturmstufe */
  stormLevel: string;
  /** K4: Feldplätze, bestimmt auch den XP-Topf (F15) */
  fieldSlots: number;
  /** K15: Rückzugsschwelle; Standard aus combat.json */
  retreat?: string;
  /** F15, R27: Faktor auf den XP-Topf (pro Tag +5 %, Boss doppelt) */
  xpFactor?: number;
  /** I26 Artefakte, die den Kampf verändern: Böen schwächer (Wurzelwerk), Blitze beflügeln (Blitzfänger) */
  mods?: { gustFactor?: number; lightningRod?: { factor: number; attacks: number } };
  /**
   * R38 (Fabi, Phase Kern): Sturmwesen werden durch das Unwetter nicht stärker, und es gibt dafür keine XP mehr; das
   * Wetter im Kampf (Blitze, Regen) bleibt. Nur wer eingeholt am Durchlass kämpft, trifft sie im vollen Unwetter
   */
  calm?: boolean;
  /**
   * K46 (Fabi, 25./26.10.2026): Echtzeit mit Pause. Freunde nehmen Befehle an (hingehen, angreifen, Kniff), ihre Kniffe
   * zünden nie von selbst. Ohne den Schalter läuft der Kampf wie bisher
   */
  commandMode?: boolean;
  /** Harness „ohne Eingreifen“: Im commandMode zünden die Kniffe der Freunde doch von selbst, mit dem Ziel, das sie früher gewählt hätten */
  autoKniffe?: boolean;
  /** K47: Fernangriffe werden echte Geschosse mit Flugzeit */
  projectiles?: boolean;
  /** K48: Sturmwesen sagen große Angriffe vorher an (Streifen, Marke, Ring) */
  telegraphs?: boolean;
  /** K49: Hindernisse auf dem Ufer (Steine, Treibholzstamm, Gebüsch) */
  obstacles?: Obstacle[];
  /** K46 Übungsufer: Leben und Heilung aller mal diesen Faktor, damit ein Kampf 60 bis 90 s dauert [ZAHL?] */
  hpFactor?: number;
};

/**
 * K49 (Fabi, 26.10.2026): ein Hindernis. Steine und Stamm sind fest: Man läuft drumherum, gerade Geschosse bleiben daran
 * hängen. Ins Gebüsch kann man hineingehen und ist dort vor Fernkämpfern versteckt. Der Stamm ist eine Kapsel von
 * (x, y) bis (x2, y2) mit dem Radius als halber Dicke
 */
export type Obstacle = { id: string; kind: 'stone' | 'log' | 'bush'; x: number; y: number; radius: number; x2?: number; y2?: number };

/** K46: ein Befehl an einen Freund. tick: ab diesem Takt gilt er (Takt n endet bei n · tickSeconds) */
export type BattleCommand =
  | { tick: number; unit: number; kind: 'move'; x: number; y: number }
  | { tick: number; unit: number; kind: 'attack'; target: number }
  | { tick: number; unit: number; kind: 'kniff'; x?: number; y?: number; target?: number }
  | { tick: number; unit: number; kind: 'clear' };

/** K46: der Befehl, den ein Freund gerade ausführt; erledigt, ist er weg und der Freund handelt wieder selbst */
export type Order =
  /** best, since: wie nah er schon war und seit wann, damit niemand ewig festhängt */
  | { kind: 'move'; x: number; y: number; best?: number; since?: number }
  | { kind: 'attack'; target: number }
  | { kind: 'kniff'; x?: number; y?: number; target?: number };

/**
 * K48: eine Ansage. stripe: Streifen am Boden von (x, y) in Richtung (dx, dy); mark: Marke am Freund target; ring: Ring
 * um das Sturmwesen. Zur Zeit at kommt der Angriff
 */
export type TelegraphShape =
  | { kind: 'stripe'; x: number; y: number; dx: number; dy: number; length: number; width: number }
  | { kind: 'mark'; target: number }
  | { kind: 'ring'; radius: number }
  /** K53 (Erste Fassung): eine Fläche um ein Ziel, etwa Auf die Augen der Dreckschleuder */
  | { kind: 'area'; target: number; radius: number };

/** K47: ein Geschoss im Flug. straight trifft den Ersten in der Bahn, arc landet am Zielort, homing kommt immer an */
export type ShotStyle = 'straight' | 'arc' | 'homing';
export type ShotPayload =
  | { kind: 'hit'; raw: number }
  | { kind: 'heal'; amount: number; via?: string; plain?: boolean }
  | { kind: 'slow'; slow: number; duration: number; via: string };
export type Shot = {
  id: number;
  from: number;
  side: Side;
  target: number;
  x: number;
  y: number;
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  speed: number;
  style: ShotStyle;
  t0: number;
  t1: number;
  /** Bild aus dem Manifest (federpfeil, hagelkorn …), Inhalt aus combat.json */
  art: string;
  payload: ShotPayload;
  /** Hindernisse, an denen der Werfer selbst steht: Aus der Deckung heraus trifft man, die Deckung schützt nur das Ziel */
  ignore: string[];
};

export type BattleOptions = {
  /** Positionen und Leben jedes Takts mitschreiben, für die Darstellung */
  recordFrames?: boolean;
  /** K46: Befehle mit ihrem Takt; derselbe Kampf mit denselben Befehlen läuft gleich ab */
  commands?: BattleCommand[];
};

export type UnitKind = 'friend' | 'stormling' | 'infested' | 'tree';
export type Side = 'friend' | 'enemy';

export type Contribution = {
  damage: number;
  taken: number;
  healed: number;
  /** verhinderter Schaden (Bau graben, Dickes Fell) */
  shielded: number;
  kills: number;
};

/** F32 (Spieltest Phase 4, Fabi): Jeder Biss wirkt seine eigene Zeit und verfällt dann; expiries hält das Ende jedes Stapels */
/** owners: wer jeden Stapel gelegt hat (gleiche Reihenfolge wie expiries), damit jeder Vergifter seinen Anteil sieht (Fabi, Phase Kern) */
export type Poison = { stacks: number; expiries: number[]; owners: number[]; nextTick: number; source: number | null };
export type HealOverTime = { perTick: number; ticksLeft: number; nextTick: number; source: number; via?: string };
export type DamageOverTime = { perTick: number; ticksLeft: number; nextTick: number; source: number; /** I25 Fingerhut, Bienenstich: Gift, wird so angezeigt */ venom?: boolean; via?: string };
/** S5 Nussknacker; lingerUntil: nach den Treffern bleibt das Eichhörnchen kurz beim Ziel (Q34, Variante a) */
/** cycle (F35 Wirbelwind, Fabi): die schwächsten Gegner reihum, 1 → 2 → 3 → 1 → 2 → 3 */
export type Leap = { targetUid: number; hitsLeft: number; nextHitAt: number; lingerUntil?: number; cycle?: number[] };

export type Unit = {
  uid: number;
  side: Side;
  kind: UnitKind;
  defId: string;
  name: string;
  friendClass: string | null;
  x: number;
  y: number;
  radius: number;
  hp: number;
  maxHp: number;
  damage: number;
  attackInterval: number;
  range: number;
  moveSpeed: number;
  critChance: number;
  attackCooldown: number;
  mut: number;
  mutPerAttack: number;
  passive: string | null;
  kniff: string | null;
  /** S5: Waschbär-Angriff heilt */
  attackHeal: number;
  behavior: EnemyBehavior;
  /** G18 Windschnitt: laufender Abstecher in die hintere Reihe */
  raid: { targetUid: number; attacksLeft: number; until: number } | null;
  /** G26 Nieselchen (Fabi, Phase Kern): versteckt sich hinter einem stärkeren Sturmwesen und wirft Blätter; ohne Versteck kämpft es aus der Nähe */
  hide: { range: number; meleeRange: number; meleeBoost: number; throwFactor?: number } | null;
  hideMelee: boolean;
  /** G26: der Sprung mit dem Blattschwert, Hiebe und kurzes Verweilen, dann zurück ins Versteck */
  pounce: { targetUid: number; hitsLeft: number; nextHitAt: number; lingerUntil?: number } | null;
  raidsStarted: number;
  /** G23 Blitzflitzer: bei wem er gerade ist und wie viele Schläge noch */
  flit: { targetUid: number; hitsLeft: number } | null;
  /** G22, G24 (Fabi, Runde 7): Dreck in den Augen oder eingeschüchtert: so viele Angriffe machen weniger Schaden */
  weakAttacks: number;
  weakFactor: number;
  /** K23 Verpeilt: wie oft er seinen Kniff schon versucht hat */
  kniffTries: number;
  confusedUntil: number;
  weakKind: 'blinded' | 'timid' | 'distracted' | null;
  /** S8 Feuersalamander, Hautgift: bis dann bekommt Gift, wer ihn aus der Nähe angreift */
  toxicUntil: number;
  /** S8 Elster, Glitzerklau: was sie Sturmwesen abgeluchst hat; fällt bei einem Sieg wie Beute */
  loot: string[];
  traits: string[];
  alive: boolean;
  targetUid: number | null;
  /** G18 (Spieltest Phase 4): auf dieses Ziel hat der Freund schon angegriffen; bis dahin nimmt er den Nächsten */
  engagedUid: number | null;
  slowUntil: number;
  slowFactor: number;
  hasteUntil: number;
  hasteFactor: number;
  guardUntil: number;
  guardReduction: number;
  guardSource: number | null;
  tauntBy: number | null;
  tauntAttacks: number;
  rolledUntil: number;
  rollUsed: boolean;
  fleeUntil: number;
  fleeUsed: boolean;
  /** K23 Feige / K15 Rückzug: hat den eigenen Rand erreicht und ist weg (Freunde: in Sicherheit) */
  escaped: boolean;
  /** K21: vom Blitz benommen */
  stunnedUntil: number;
  enragedUntil: number;
  rageUsed: boolean;
  retargetAt: number;
  marked: boolean;
  markedBy: number | null;
  poison: Poison;
  hots: HealOverTime[];
  /** Q49 Gründlich (Fabi, Variante a): Schaden, der nachwirkt */
  dots: DamageOverTime[];
  /** K6 (Fabi): so viel Mut braucht dieses Wesen für seinen Kniff */
  mutMax: number;
  /** K27: läuft zu Kampfbeginn noch dorthin, längstens bis marchUntil */
  march: { x: number; y: number } | null;
  marchUntil: number;
  /** K21 (Fabi, Spieltest Phase 4): wie sehr er schon am Rand gegen Wind und Stöße ankämpft; zu viel, und er ist draußen */
  edgeStrain: number;
  /** über den Rand geschubst: wartet dort bis zum Ende des Kampfs, nimmt keinen Abschied, bekommt keine XP */
  pushedOut: boolean;
  /** S5 (Fabi): vom Baum gepustet, klettert bis dahin wieder hoch */
  climbUntil: number;
  /** K21 (Fabi): wann ein Gegner das nächste Mal schaut, ob ein näheres oder leichteres Ziel vor ihm steht */
  switchAt: number;
  /** Eingesteckter Schaden der letzten Sekunden (Schutzpatron, Vorratskammer: wer gerade viel abbekommt) */
  hitLog: { t: number; amount: number }[];
  /** I25 Spechtfeder: Angriffe seit dem letzten sicheren Volltreffer */
  critCount: number;
  /** I25 Staubpilz: Die Sporenwolke ist verpufft */
  sporeUsed: boolean;
  /** I31 Bernsteintropfen: bis dann tut ihm kein Treffer weh; einmal je Kampf */
  amberUntil: number;
  amberUsed: boolean;
  /** Eichhörnchen: sein Baum, sein Platz, ob es gerade oben sitzt, sein Sprung */
  treeUid: number | null;
  homeX: number;
  homeY: number;
  onTree: boolean;
  leap: Leap | null;
  stats: Contribution;
  /** Kennung des Freundes im Run */
  runId: string | null;
  /** Leben beim Betreten des Ufers (R11, K15) */
  startHp: number;
  /** Zahlen dieses Wesens, Schlüssel „faehigkeit.wert“; fehlt einer, gilt der Wert aus abilities.json */
  params: Record<string, number>;
  /** Sonderwirkungen (CombatProfile.perks) */
  perks: Record<string, number>;
  /** Fabi (Phase Kern): woher jede Sonderwirkung kommt (Item, Ast, Klassenmerkmal), für Verlauf und Karte */
  perkFrom: Record<string, string[]>;
  /** S8 Nachwachsen: gesammelte Erholung, die einmal pro Sekunde im Verlauf landet */
  regenBank: number;
  /** F35 Schild: fängt so viel Schaden ab, bevor Leben verloren geht */
  shield: number;
  shieldSource: number | null;
  kniffUses: number;
  /** F35 Stachelkugel: eingerollt, behält Angreifer, wirft Schaden zurück */
  spikyUntil: number;
  /** F35 Stachelball: kreiselt auf einem Gegner; point (K50): kreiselt an seinem Ort und trifft alle um sich herum */
  spin: { targetUid: number; started: number; until: number; nextTick: number; point?: boolean } | null;
  /** R27 Stoßtauchen: im Anflug auf dieses Ziel, der Stoß kommt zu diesem Zeitpunkt */
  dive: { targetUid: number; at: number } | null;
  /** R27: Diese Markierung löst nur der Eisvogel aus, der gerade zustößt */
  markReservedBy: number | null;
  /** G18/F35: Einheiten, die schneller laufen, solange sie ein Ziel ansteuern (Stachelball) */
  rushUntil: number;
  /** R27 Erdüberraschung: unter der Erde, niemand kann ihn angreifen */
  burrowUntil: number;
  /** R27 Nussgeschenk, Raserei: macht eine Weile mehr Schaden */
  empowerUntil: number;
  empowerFactor: number;
  /** I18, G21: getragene Items */
  items: string[];
  /** E6: Level eines Befallenen */
  level: number;
  /** F42 (Fabi, Phase Kern): kam verwundet ans Ufer */
  wounded: boolean;
  /** S10 Leibwache: sein Schützling; unterwegs zu ihm bis until */
  wardUid: number | null;
  /** B25: verbunden aus dem Gemeinschaftsraum, mit wem (uids), wie stark und wie nah */
  bond: { with: number[]; bonus: number; reach: number } | null;
  /** S13 Otter, Pfote halten: solange einer von with nah ist, weniger Schaden */
  paw: { with: number[]; reduction: number; reach: number } | null;
  guardRun: { wardUid: number; until: number } | null;
  /** K46: der Befehl, den er gerade ausführt */
  order: Order | null;
  /**
   * K46: unterwegs auf eigene Art: tunnel (der Dachs wühlt sich zum Bau), roll (der Igel rollt zum Stachelball), beckon
   * (herbeigewinkt vom Waschbär). safe: unterwegs nimmt er keinen Schaden und ist nicht anzugreifen
   */
  travel: { kind: 'tunnel' | 'roll' | 'beckon'; x: number; y: number; speed: number; safe: boolean; by: number | null; until: number; /** erst eingraben, dann los */ startAt?: number } | null;
  /** K50 Bau graben (neu): gräbt gerade, danach sackt der Boden ab */
  digUntil: number;
  /** K50: schlittert in die Mulde zu diesem Punkt; danach steckt er bis stuckUntil fest */
  slide: { x: number; y: number; until: number; speed: number } | null;
  stuckUntil: number;
  /** K48: holt aus für einen angesagten Angriff */
  windup: { id: number; kniff: string; at: number; shape: TelegraphShape; target: number | null } | null;
  /** K51: Diese Schwachstelle bleibt, bis ein Kniff sie trifft (sonst gilt die alte Regel: der nächste Treffer) */
  markHold: boolean;
  /** K51: Ein Kniff hat die Schwachstelle getroffen; seine Treffer bis until zählen doppelt */
  markClaim: { by: number; until: number } | null;
  /** K52 Sturzflug (Passive): stürzt auf target, trifft bei at, ist bis back wieder an seinem Platz */
  swoop: { target: number; at: number; back: number; hit: boolean } | null;
};

export type Outcome = 'win' | 'loss' | 'retreat';

export type StatusName = 'rolled' | 'taunted' | 'slowed' | 'hasted' | 'guarded' | 'fleeing' | 'returned' | 'enraged' | 'stunned' | 'retreating' | 'spiky' | 'spinning' | 'shielded' | 'revived' | 'burrowed' | 'empowered' | 'climbing' | 'blinded' | 'timid' | 'confused' | 'distracted' | 'toxic' | 'guarding' | 'amber' | 'sliding' | 'stuck';

export type BattleEvent =
  /** via (Fabi, Phase Kern): woher der Schaden oder die Heilung kommt, etwa 'kniff:kieselwurf', 'item:holunderkette', 'class:waschbaer:adlerauge'; mehrere mit | getrennt */
  /** blocked (Fabi, Phase Kern): so viel hat ein Schild abgefangen, blockedBy: wer den Schild gegeben hat */
  /** spotOpen (K51): dieser Treffer hat die Schwachstelle geöffnet; der Sturzflug folgt */
  | { t: number; type: 'damage'; from: number | null; to: number; amount: number; source: 'hit' | 'kniff' | 'poison' | 'lightning' | 'reflect' | 'recoil' | 'splash' | 'dot' | 'thistle'; weakSpot: boolean; crit: boolean; via?: string; blocked?: number; blockedBy?: number; spotOpen?: boolean }
  /** bounce (Fabi, Phase Kern): Die Heilung ist von diesem Wesen abgeprallt, etwa die Nuss beim Nusshagel */
  | { t: number; type: 'heal'; from: number; to: number; amount: number; via?: string; bounce?: number }
  /** x, y (K46): der Ort, auf den ein Kniff mit Ortsziel geht */
  | { t: number; type: 'kniff'; unit: number; kniff: string; target: number | null; x?: number; y?: number }
  /** T31 (Phase Kern): ein normaler Angriff, eine Heilung oder ein Dreckschild beginnt jetzt; nur für die Darstellung */
  | { t: number; type: 'strike'; unit: number; target: number; kind: 'hit' | 'heal' | 'shield' }
  | { t: number; type: 'mark'; by: number; to: number }
  | { t: number; type: 'status'; unit: number; status: StatusName; until: number; via?: string }
  | { t: number; type: 'poison'; unit: number; stacks: number }
  | { t: number; type: 'poisonJump'; from: number; to: number; stacks: number; source: number }
  /** I25 Staubpilz: Die Sporenwolke platzt */
  | { t: number; type: 'spores'; unit: number }
  | { t: number; type: 'pushedOut'; unit: number }
  /** scared: S8 Gezeter, weicht erschrocken vor der Elster zurück */
  | { t: number; type: 'move'; unit: number; x: number; y: number; reason: 'leap' | 'return' | 'knockback' | 'scared' | 'charge' }
  | { t: number; type: 'down'; unit: number; by: number | null }
  | { t: number; type: 'escape'; unit: number }
  | { t: number; type: 'retreat' }
  | { t: number; type: 'gust'; dx: number; dy: number; duration: number }
  | { t: number; type: 'lightningWarning'; x: number; y: number; radius: number; strikeAt: number }
  | { t: number; type: 'lightning'; x: number; y: number; radius: number }
  | { t: number; type: 'heavyRain'; multiplier: number }
  /** K54: Kräftemessen beginnt; ab at werden Treffer härter */
  | { t: number; type: 'duel'; at: number }
  | { t: number; type: 'end'; outcome: Outcome; reason: 'allEnemiesDown' | 'allFriendsDown' | 'retreat' | 'timeout' }
  /** K46: ein Befehl gilt ab jetzt (für Darstellung und Wiederholung) */
  | { t: number; type: 'order'; unit: number; order: Order | null }
  /** K47: ein Geschoss fliegt los; arrive: wann es am Zielort ist (homing: ungefähr) */
  | { t: number; type: 'shot'; id: number; from: number; to: number; x: number; y: number; tx: number; ty: number; style: ShotStyle; art: string; arrive: number }
  /** K47: das Geschoss ist angekommen: getroffen (unit), an einem Hindernis hängen geblieben (obstacle) oder ins Leere */
  | { t: number; type: 'shotEnd'; id: number; x: number; y: number; unit: number | null; obstacle: string | null }
  /** K48: ein Sturmwesen sagt einen Angriff an; telegraphEnd: er kommt (done) oder fällt aus */
  | { t: number; type: 'telegraph'; id: number; unit: number; kniff: string; at: number; shape: TelegraphShape }
  | { t: number; type: 'telegraphEnd'; id: number; unit: number; done: boolean }
  /** K52 Sturzflug (Passive): der Eisvogel stürzt los, at: wann er trifft */
  | { t: number; type: 'swoop'; unit: number; target: number; at: number };

/** Zustände einer Einheit als Bitmaske im Bild, damit die Darstellung sie zeigen kann */
export const FrameFlag = {
  alive: 1,
  onTree: 2,
  rolled: 4,
  guarded: 8,
  marked: 16,
  fleeing: 32,
  enraged: 64,
  hasted: 128,
  slowed: 256,
  poisoned: 512,
  taunted: 1024,
  leaping: 2048,
  stunned: 4096,
  spinning: 8192,
  shielded: 16384,
  burrowed: 32768,
  empowered: 65536,
  pushedOut: 131072,
  climbing: 262144,
  /** G22, G24, K23 (Runde 7) */
  blinded: 524288,
  timid: 1048576,
  confused: 2097152,
  distracted: 4194304,
  toxic: 8388608,
  /** S10 Leibwache: eilt gerade zu seinem Schützling */
  guarding: 16777216,
} as const;

/** K46: weitere Zustände für Echtzeit mit Pause, als zweite Bitmaske im Bild (Feld 8) */
export const FrameFlag2 = {
  /** herbeigewinkt unterwegs */
  beckoned: 1,
  /** der Dachs wühlt sich unter der Erde zum Bau */
  tunneling: 2,
  /** der Igel rollt zum Stachelball */
  rolling: 4,
  /** schlittert in die Mulde */
  sliding: 8,
  /** steckt in der Mulde fest */
  stuck: 16,
  /** gräbt gerade seinen Bau */
  digging: 32,
  /** steht im Gebüsch */
  hidden: 64,
  /** holt aus für einen angesagten Angriff */
  windup: 128,
  /** Kniff bereit (Mut voll) */
  ready: 256,
  /** im Sturzflug */
  swooping: 512,
} as const;

/** Ein Bild pro Takt: je Einheit [x, y, hp, mut, flags, Giftstapel (mit Gift von Items), Ziel (uid oder -1), Schild, flags2] in uid-Reihenfolge */
export type Frame = { t: number; units: [number, number, number, number, number, number, number, number, number?][] };

export type FriendSummary = {
  uid: number;
  /** Kennung des Freundes im Run */
  id: string | null;
  name: string;
  species: string;
  friendClass: string;
  maxHp: number;
  hpEnd: number;
  /** F21, F42 (Phase Kern): Abschied nimmt, wer verwundet noch einmal fällt (oder beim ersten Fall, wenn der Schalter an ist) */
  farewell: boolean;
  /** F42: im Kampf gefallen; ohne Abschied kommt er verwundet zurück. downBy: wer ihn zu Fall gebracht hat */
  fell: boolean;
  downBy: string | null;
  /** K21 (Fabi): über den Rand geschubst, wartet dort und kommt mit seinem Leben zurück aufs Boot */
  pushedOut: boolean;
  /** I25 Staubpilz: Die Sporenwolke ist in diesem Kampf verpufft */
  sporeUsed?: boolean;
  kniffUses: number;
  /** S5: Leben des Baums am Ende; null ohne Baum */
  treeHpEnd: number | null;
  stats: Contribution;
  /** F15: XP aus dem Topf; wer Abschied nimmt, bekommt nichts, sein Anteil geht an die Überlebenden */
  xp: number;
};

export type BattleResult = {
  outcome: Outcome;
  duration: number;
  xpPot: number;
  /** K23: entkommene Gegner, sie verkleinern den Topf */
  escaped: number;
  /** So viel XP hat der Topf durch entkommene Gegner verloren */
  xpLostToEscapes: number;
  friends: FriendSummary[];
  /** E6: befreite Befallene (vertrieben heißt befreit) */
  freed: { uid: number; species: string; friendClass: string; name: string; level: number }[];
  /** I18: Items vertriebener Gegner */
  drops: string[];
  units: Unit[];
  events: BattleEvent[];
  frames: Frame[];
};
